import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { observer } from 'mobx-react-lite';
import type { BillingInterval, BillingPricingTier } from 'we-tree-api-client/v2';
import { domainStore } from '@/stores/DomainStore';
import { billingActivatingSuccessUrl, clearBillingActivating, markBillingActivating } from '@/utils/billingActivation';
import { isPaddleConfigured, openPaddleCheckout } from '@/utils/paddleCheckout';

import './upgrade-family-plan-modal.css';

export type UpgradeModalPhase = 'choose' | 'opening' | 'checkout' | 'activating';

export interface UpgradeFamilyPlanModalProps {
  familyId: string;
  isOpen: boolean;
  onClose: () => void;
  /** Open directly on activating (e.g. after Paddle “Return to …” navigation). */
  initialPhase?: UpgradeModalPhase;
}

const PRICE_COPY: Record<BillingPricingTier, Record<BillingInterval, string>> = {
  founding: { month: '$4.99/mo', year: '$39.99/yr' },
  standard: { month: '$6.99/mo', year: '$49.99/yr' }
};

/**
 * Owner-only Family plan upgrade: interval choice → API checkout params → Paddle overlay.
 * After payment, shows optimistic "activating…" without flipping entitlements locally.
 */
const UpgradeFamilyPlanModal: React.FC<UpgradeFamilyPlanModalProps> = observer(
  ({ familyId, isOpen, onClose, initialPhase = 'choose' }) => {
    const { t } = useTranslation('forms');
    const { t: tCommon } = useTranslation('common');
    const [interval, setInterval] = useState<BillingInterval>('year');
    const [phase, setPhase] = useState<UpgradeModalPhase>(initialPhase);
    const [pricingTier, setPricingTier] = useState<BillingPricingTier | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleOpenChange = useCallback(
      (open: boolean) => {
        if (!open && phase !== 'opening') {
          if (phase === 'activating') {
            clearBillingActivating(familyId);
          }
          onClose();
        }
      },
      [onClose, phase, familyId]
    );

    useEffect(() => {
      if (isOpen) {
        setInterval('year');
        setPhase(initialPhase);
        setPricingTier(null);
        setErrorMessage(null);
      }
    }, [isOpen, initialPhase]);

    const handleContinue = async () => {
      setErrorMessage(null);
      if (!isPaddleConfigured()) {
        setErrorMessage(t('upgradePlan.errors.paddleNotConfigured'));
        return;
      }

      setPhase('opening');
      try {
        const params = await domainStore.prepareUpgradeCheckout(familyId, interval);
        setPricingTier(params.pricingTier);
        await openPaddleCheckout({
          familyId,
          priceId: params.priceId,
          customerId: params.customerId,
          customData: params.customData,
          successUrl: billingActivatingSuccessUrl(familyId),
          onCompleted: () => {
            markBillingActivating(familyId);
            setPhase('activating');
          },
          onClosed: () => {
            setPhase((current) => (current === 'activating' ? current : 'choose'));
          }
        });
        setPhase('checkout');
      } catch (err) {
        setPhase('choose');
        setErrorMessage(err instanceof Error ? err.message : t('upgradePlan.errors.checkoutFailed'));
      }
    };

    const handleDone = () => {
      clearBillingActivating(familyId);
      onClose();
    };

    const foundingHint =
      pricingTier === 'founding'
        ? t('upgradePlan.foundingHint')
        : pricingTier === 'standard'
          ? t('upgradePlan.standardHint')
          : t('upgradePlan.defaultHint');

    return (
      <Dialog.Root open={isOpen} onOpenChange={handleOpenChange}>
        <Dialog.Portal>
          <Dialog.Overlay className="v01-modal-overlay v01-modal-overlay--centered">
            <Dialog.Content
              className="v01-modal v01-modal--panel upgrade-family-plan-modal"
              aria-labelledby="upgrade-family-plan-title"
              aria-describedby="upgrade-family-plan-desc"
            >
              {phase === 'activating' ? (
                <>
                  <div className="v01-modal__header v01-modal__header--compact">
                    <h2 id="upgrade-family-plan-title" className="v01-modal__title">
                      {t('upgradePlan.activatingTitle')}
                    </h2>
                  </div>
                  <p id="upgrade-family-plan-desc" className="upgrade-family-plan-modal__lede">
                    {t('upgradePlan.activatingLede')}
                  </p>
                  <div className="upgrade-family-plan-modal__actions">
                    <button type="button" className="btn btn--primary" onClick={handleDone}>
                      {tCommon('done')}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="v01-modal__header v01-modal__header--compact">
                    <h2 id="upgrade-family-plan-title" className="v01-modal__title">
                      {t('upgradePlan.title')}
                    </h2>
                  </div>
                  <p id="upgrade-family-plan-desc" className="upgrade-family-plan-modal__lede">
                    {t('upgradePlan.lede')}
                  </p>
                  <p className="upgrade-family-plan-modal__hint">{foundingHint}</p>

                  <fieldset
                    className="upgrade-family-plan-modal__fieldset"
                    disabled={phase === 'opening' || phase === 'checkout'}
                  >
                    <legend className="upgrade-family-plan-modal__legend">{t('upgradePlan.intervalLegend')}</legend>
                    <RadioGroup.Root
                      className="upgrade-family-plan-modal__radios"
                      value={interval}
                      onValueChange={(value) => setInterval(value as BillingInterval)}
                      aria-label={t('upgradePlan.intervalAria')}
                    >
                      <label className="upgrade-family-plan-modal__radio">
                        <RadioGroup.Item value="year" className="upgrade-family-plan-modal__radio-item" />
                        <span>
                          {t('upgradePlan.annual')}
                          <span className="upgrade-family-plan-modal__price">
                            {t('upgradePlan.foundingPrice', { price: PRICE_COPY.founding.year })} ·{' '}
                            {t('upgradePlan.standardPrice', { price: PRICE_COPY.standard.year })}
                          </span>
                        </span>
                      </label>
                      <label className="upgrade-family-plan-modal__radio">
                        <RadioGroup.Item value="month" className="upgrade-family-plan-modal__radio-item" />
                        <span>
                          {t('upgradePlan.monthly')}
                          <span className="upgrade-family-plan-modal__price">
                            {t('upgradePlan.foundingPrice', { price: PRICE_COPY.founding.month })} ·{' '}
                            {t('upgradePlan.standardPrice', { price: PRICE_COPY.standard.month })}
                          </span>
                        </span>
                      </label>
                    </RadioGroup.Root>
                  </fieldset>

                  {phase === 'checkout' ? (
                    <p className="upgrade-family-plan-modal__hint">{t('upgradePlan.checkoutHint')}</p>
                  ) : null}

                  {errorMessage ? (
                    <p className="upgrade-family-plan-modal__error" role="alert">
                      {errorMessage}
                    </p>
                  ) : null}

                  <div className="upgrade-family-plan-modal__actions">
                    <button type="button" className="btn btn--ghost" onClick={onClose} disabled={phase === 'opening'}>
                      {tCommon('cancel')}
                    </button>
                    <button
                      type="button"
                      className="btn btn--primary"
                      onClick={handleContinue}
                      disabled={phase === 'opening' || phase === 'checkout'}
                    >
                      {phase === 'opening'
                        ? t('actions.openingCheckout')
                        : phase === 'checkout'
                          ? t('actions.checkoutOpen')
                          : t('actions.continueToCheckout')}
                    </button>
                  </div>
                </>
              )}
            </Dialog.Content>
          </Dialog.Overlay>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }
);

export default UpgradeFamilyPlanModal;
